import { useState, useEffect } from "react";
import { Select } from "antd";
import { Link, useSearchParams } from "react-router-dom";
import { listAllReports, listSubscriptions, ReportItem, SubscriptionItem } from "../../../../services/subscription";
import { formatBeijingDateTime } from "../../../../utils/datetime";
import RIcon from "../../../../components/RIcon";

const STATUS_CONFIG: Record<string, { label: string; cls: string; icon: string }> = {
  completed: { label: "已完成", cls: "bg-emerald-50 text-emerald-600", icon: "ri-check-circle-line" },
  generating: { label: "生成中", cls: "bg-blue-50 text-blue-500", icon: "ri-loader-4-line" },
  failed: { label: "失败", cls: "bg-red-50 text-red-500", icon: "ri-error-warning-line" },
};

export default function ReportsPage() {
  const [searchParams] = useSearchParams();
  const initSubId = searchParams.get("sub") ?? "all";
  const [filterSub, setFilterSub] = useState<number | "all">(initSubId === "all" ? "all" : parseInt(initSubId));
  const [filterStatus, setFilterStatus] = useState<string | "all">("all");
  const [search, setSearch] = useState("");
  const [reports, setReports] = useState<ReportItem[]>([]);
  const [subscriptions, setSubscriptions] = useState<SubscriptionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let pollTimer: ReturnType<typeof setTimeout> | null = null;

    const load = (silent: boolean) => {
      if (!silent) setLoading(true);
      Promise.all([listAllReports(), listSubscriptions()])
        .then(([reportList, subList]) => {
          if (cancelled) return;
          setReports(reportList);
          setSubscriptions(subList);
          // 如果还有 generating 状态的报告，每 5 秒轮询一次直到全部 completed/failed
          if (reportList.some((r) => r.status === "generating")) {
            pollTimer = setTimeout(() => load(true), 5000);
          }
        })
        .catch((err) => {
          console.error("Failed to load reports or subscriptions:", err);
        })
        .finally(() => {
          if (!cancelled && !silent) setLoading(false);
        });
    };

    load(false);

    return () => {
      cancelled = true;
      if (pollTimer) clearTimeout(pollTimer);
    };
  }, []);

  const filtered = reports.filter(r => {
    if (filterSub !== "all" && r.subscription_id !== filterSub) return false;
    if (filterStatus !== "all" && r.status !== filterStatus) return false;
    if (search && !r.title.includes(search) && !r.subscription_name.includes(search)) return false;
    return true;
  });

  return (
    <div className="p-6 space-y-5 min-h-full bg-gray-50/40">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/workbench/content-sub" className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 cursor-pointer text-[#718096]">
            <RIcon name="arrow-left-line" />
          </Link>
          <div>
            <h1 className="text-xl font-bold text-[#1A2B3C]">采集报告</h1>
            <p className="text-sm text-[#718096] mt-0.5">共 {filtered.length} 份报告</p>
          </div>
        </div>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {[
          { label: "报告总数", value: reports.length, color: "text-[#0EA5A0] bg-[#0EA5A0]/10", icon: "ri-file-chart-line" },
          { label: "已完成", value: reports.filter(r => r.status === "completed").length, color: "text-emerald-600 bg-emerald-50", icon: "ri-check-circle-line" },
          { label: "失败", value: reports.filter(r => r.status === "failed").length, color: "text-red-500 bg-red-50", icon: "ri-error-warning-line" },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-100 p-4 flex items-center gap-3">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${s.color}`}>
              <i className={`${s.icon} text-base`} />
            </div>
            <div>
              <div className="text-lg font-bold text-[#1A2B3C]">{s.value}</div>
              <div className="text-xs text-[#718096]">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-100 p-4 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-48">
          <RIcon name="search-line" className="absolute left-3 top-1/2 -translate-y-1/2 text-[#718096] text-sm" />
          <input
            className="w-full pl-8 pr-3 h-9 border border-gray-200 rounded-lg text-sm outline-none focus:border-[#0EA5A0]"
            placeholder="搜索报告标题..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <Select
          showSearch
          style={{ width: 220 }}
          value={filterSub === "all" ? "all" : String(filterSub)}
          onChange={(v) => setFilterSub(v === "all" ? "all" : parseInt(v))}
          optionFilterProp="label"
          options={[
            { value: "all", label: "全部订阅" },
            ...subscriptions.map((s) => ({ value: String(s.id), label: s.name })),
          ]}
        />
        <div className="flex gap-1 border border-gray-200 rounded-lg p-1">
          {([
            { value: "all", label: "全部" },
            { value: "completed", label: "已完成" },
            { value: "generating", label: "生成中" },
            { value: "failed", label: "失败" },
          ] as { value: string | "all"; label: string }[]).map(opt => (
            <button
              key={opt.value}
              onClick={() => setFilterStatus(opt.value)}
              className={`px-3 h-7 text-xs rounded-md cursor-pointer whitespace-nowrap transition-colors ${filterStatus === opt.value ? "bg-[#0EA5A0] text-white" : "text-[#718096] hover:bg-gray-50"}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Report List */}
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
              <RIcon name="loader-4-line" className="text-xl text-[#718096] animate-spin" />
            </div>
            <p className="text-[#4A5568]">加载中...</p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3 bg-gray-100 rounded-full">
              <RIcon name="file-chart-line" className="text-xl text-[#718096]" />
            </div>
            <p className="text-[#4A5568]">暂无报告</p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/60">
                <th className="text-left text-xs font-medium text-[#718096] px-5 py-3">报告标题</th>
                <th className="text-left text-xs font-medium text-[#718096] px-4 py-3 hidden md:table-cell">所属订阅</th>
                <th className="text-left text-xs font-medium text-[#718096] px-4 py-3 hidden lg:table-cell">采集文章</th>
                <th className="text-left text-xs font-medium text-[#718096] px-4 py-3">状态</th>
                <th className="text-left text-xs font-medium text-[#718096] px-4 py-3">生成时间</th>
                <th className="text-right text-xs font-medium text-[#718096] px-5 py-3">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.map(report => {
                const sc = STATUS_CONFIG[report.status] ?? { label: report.status, cls: "bg-gray-100 text-[#718096]", icon: "ri-file-line" };
                return (
                  <tr key={report.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-[#1A2B3C] leading-snug">{report.title}</div>
                      <div className="text-xs text-[#718096] mt-0.5 line-clamp-1 max-w-xs">{report.summary}</div>
                    </td>
                    <td className="px-4 py-4 hidden md:table-cell">
                      <span className="text-xs bg-gray-100 text-[#4A5568] px-2 py-1 rounded-md">{report.subscription_name}</span>
                    </td>
                    <td className="px-4 py-4 hidden lg:table-cell">
                      <span className="text-sm font-medium text-[#1A2B3C]">{report.articles_count > 0 ? report.articles_count : "—"}</span>
                      {report.articles_count > 0 && <span className="text-xs text-[#718096] ml-0.5">篇</span>}
                    </td>
                    <td className="px-4 py-4">
                      <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium ${sc.cls}`}>
                        <div className="w-3.5 h-3.5 flex items-center justify-center">
                          <i className={`${sc.icon} ${report.status === "generating" ? "animate-spin" : ""}`} />
                        </div>
                        {sc.label}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-xs text-[#4A5568]">{formatBeijingDateTime(report.title.match(/(\d{4}-\d{2}-\d{2} \d{2}:\d{2})$/)?.[1] || report.created_at)}</div>
                      <div className="text-xs text-[#718096]">{report.duration}</div>
                    </td>
                    <td className="px-5 py-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {report.status === "completed" && (
                          <>
                            <Link
                              to={`/workbench/content-sub/reports/${report.id}`}
                              className="text-xs px-3 h-7 bg-[#0EA5A0]/10 text-[#0EA5A0] hover:bg-[#0EA5A0]/20 rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1"
                            >
                              <div className="w-3 h-3 flex items-center justify-center"><RIcon name="eye-line" /></div>
                              查看
                            </Link>
                            {report.file_id && (
                              <Link
                                to={`/workbench/files?id=${report.file_id}`}
                                className="text-xs px-3 h-7 border border-gray-200 text-[#4A5568] hover:border-[#0EA5A0] hover:text-[#0EA5A0] rounded-lg cursor-pointer whitespace-nowrap flex items-center gap-1"
                              >
                                <div className="w-3 h-3 flex items-center justify-center"><RIcon name="folder-line" /></div>
                                文件
                              </Link>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
